import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_BASE = '';

function BuchhaltungManager() {
    const { user, authFetch } = useAuth();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Dashboard State
    const [dashboard, setDashboard] = useState(null);

    // Belege State
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    // Bank State
    const [transactions, setTransactions] = useState([]);
    const [importingCsv, setImportingCsv] = useState(false);

    // Reports State
    const [eurReport, setEurReport] = useState(null);
    const [ustReport, setUstReport] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));

    // Fristen State
    const [reminders, setReminders] = useState([]);
    const [newReminder, setNewReminder] = useState({ title: '', due_date: '', type: 'ust', notes: '' });

    // Chat State
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);

    // Token direkt aus user-Objekt oder localStorage holen
    const getToken = () => user?.token || localStorage.getItem('authToken');

    const getAuthHeaders = () => ({
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
    });

    // Daten laden
    const loadDashboard = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/buchhaltung/dashboard`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setDashboard(await res.json());
            }
        } catch (err) {
            console.error('Dashboard laden fehlgeschlagen:', err);
        }
    }, []);

    const loadExpenses = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/expenses`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setExpenses(await res.json());
            }
        } catch (err) {
            console.error('Belege laden fehlgeschlagen:', err);
        }
    }, []);

    const loadCategories = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/tax/categories`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setCategories(await res.json());
            }
        } catch (err) {
            console.error('Kategorien laden fehlgeschlagen:', err);
        }
    }, []);

    const loadTransactions = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/bank/transactions`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setTransactions(await res.json());
            }
        } catch (err) {
            console.error('Transaktionen laden fehlgeschlagen:', err);
        }
    }, []);

    const loadReminders = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/tax/reminders`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setReminders(await res.json());
            }
        } catch (err) {
            console.error('Fristen laden fehlgeschlagen:', err);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
        loadCategories();
    }, [loadDashboard, loadCategories]);

    useEffect(() => {
        if (activeTab === 'belege') loadExpenses();
        if (activeTab === 'bank') loadTransactions();
        if (activeTab === 'fristen') loadReminders();
    }, [activeTab, loadExpenses, loadTransactions, loadReminders]);

    // Beleg hochladen
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploadingFile(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch(`${API_BASE}/api/admin/expenses`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`
                },
                body: formData
            });

            const data = await res.json();
            if (res.ok) {
                setSuccess(data.message || 'Beleg erfolgreich hochgeladen');
                loadExpenses();
                loadDashboard();
            } else {
                setError(data.error || 'Fehler beim Hochladen');
            }
        } catch (err) {
            setError('Netzwerkfehler: ' + err.message);
        } finally {
            setUploadingFile(false);
            event.target.value = '';
        }
    };

    // Beleg loeschen
    const deleteExpense = async (id) => {
        if (!window.confirm('Beleg wirklich loeschen?')) return;

        try {
            const res = await fetch(`${API_BASE}/api/admin/expenses/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setSuccess('Beleg geloescht');
                loadExpenses();
                loadDashboard();
            }
        } catch (err) {
            setError('Fehler beim Loeschen');
        }
    };

    // Beleg aktualisieren
    const updateExpense = async (expense) => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/expenses/${expense.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(expense)
            });
            if (res.ok) {
                setSuccess('Beleg aktualisiert');
                setEditingExpense(null);
                loadExpenses();
            }
        } catch (err) {
            setError('Fehler beim Aktualisieren');
        }
    };

    // CSV Import
    const handleCsvImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setImportingCsv(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch(`${API_BASE}/api/admin/bank/import`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`
                },
                body: formData
            });

            const data = await res.json();
            if (res.ok) {
                setSuccess(data.message);
                loadTransactions();
                loadDashboard();
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError('Import fehlgeschlagen');
        } finally {
            setImportingCsv(false);
            event.target.value = '';
        }
    };

    // EUeR Report laden
    const loadEurReport = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/admin/tax/eur/${selectedYear}`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setEurReport(await res.json());
            }
        } catch (err) {
            setError('EUeR konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    // USt Report laden
    const loadUstReport = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/admin/tax/ust/${selectedYear}/${selectedQuarter}?type=quarterly`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                setUstReport(await res.json());
            }
        } catch (err) {
            setError('USt konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    // Frist hinzufuegen
    const addReminder = async () => {
        if (!newReminder.title || !newReminder.due_date) {
            setError('Titel und Faelligkeitsdatum erforderlich');
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/admin/tax/reminders`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(newReminder)
            });
            if (res.ok) {
                setSuccess('Frist hinzugefuegt');
                setNewReminder({ title: '', due_date: '', type: 'ust', notes: '' });
                loadReminders();
            }
        } catch (err) {
            setError('Fehler beim Hinzufuegen');
        }
    };

    // Frist als erledigt markieren
    const toggleReminderComplete = async (reminder) => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/tax/reminders/${reminder.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ ...reminder, is_completed: !reminder.is_completed })
            });
            if (res.ok) {
                loadReminders();
            }
        } catch (err) {
            setError('Fehler beim Aktualisieren');
        }
    };

    // Chat senden
    const sendChatMessage = async () => {
        if (!chatInput.trim()) return;

        const userMessage = chatInput;
        setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setChatInput('');
        setChatLoading(true);

        try {
            const res = await fetch(`${API_BASE}/api/admin/ai/chat`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ message: userMessage })
            });

            const data = await res.json();
            if (res.ok) {
                setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
            } else {
                setChatMessages(prev => [...prev, { role: 'assistant', content: 'Fehler: ' + data.error }]);
            }
        } catch (err) {
            setChatMessages(prev => [...prev, { role: 'assistant', content: 'Netzwerkfehler' }]);
        } finally {
            setChatLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('de-DE');
    };

    // Dashboard Tab
    const renderDashboard = () => (
        <div>
            <h4>Uebersicht</h4>
            {dashboard ? (
                <div className="row">
                    <div className="col-md-4 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <h6>Einnahmen (Monat)</h6>
                                <h3>{formatCurrency(dashboard.aktueller_monat?.einnahmen)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className="card bg-danger text-white">
                            <div className="card-body">
                                <h6>Ausgaben (Monat)</h6>
                                <h3>{formatCurrency(dashboard.aktueller_monat?.ausgaben)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <h6>Belege (Monat)</h6>
                                <h3>{dashboard.aktueller_monat?.belege_count || 0}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 mb-3">
                        <div className="card">
                            <div className="card-body">
                                <h6>Offene Aufgaben</h6>
                                <p>Unzugeordnete Transaktionen: <strong>{dashboard.offene_aufgaben?.unzugeordnete_transaktionen || 0}</strong></p>
                                <p>Naechste USt faellig: <strong>{formatDate(dashboard.naechste_ust_faellig)}</strong></p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 mb-3">
                        <div className="card">
                            <div className="card-body">
                                <h6>Anstehende Fristen</h6>
                                {dashboard.offene_aufgaben?.fristen?.length > 0 ? (
                                    <ul className="list-unstyled mb-0">
                                        {dashboard.offene_aufgaben.fristen.map(f => (
                                            <li key={f.id}>{f.title} - {formatDate(f.due_date)}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-muted mb-0">Keine offenen Fristen</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <p>Laden...</p>
            )}
        </div>
    );

    // Belege Tab
    const renderBelege = () => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Belege / Ausgaben</h4>
                <div>
                    <input
                        type="file"
                        id="belegUpload"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="belegUpload" className="btn btn-primary">
                        {uploadingFile ? 'Analysiere...' : 'Beleg hochladen'}
                    </label>
                </div>
            </div>

            {editingExpense && (
                <div className="card mb-3">
                    <div className="card-body">
                        <h5>Beleg bearbeiten</h5>
                        <div className="row">
                            <div className="col-md-3 mb-2">
                                <label>Datum</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={editingExpense.date?.split('T')[0] || ''}
                                    onChange={e => setEditingExpense({ ...editingExpense, date: e.target.value })}
                                />
                            </div>
                            <div className="col-md-2 mb-2">
                                <label>Betrag</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-control"
                                    value={editingExpense.amount || ''}
                                    onChange={e => setEditingExpense({ ...editingExpense, amount: e.target.value })}
                                />
                            </div>
                            <div className="col-md-2 mb-2">
                                <label>MwSt</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-control"
                                    value={editingExpense.vat_amount || ''}
                                    onChange={e => setEditingExpense({ ...editingExpense, vat_amount: e.target.value })}
                                />
                            </div>
                            <div className="col-md-2 mb-2">
                                <label>MwSt-Satz</label>
                                <select
                                    className="form-control"
                                    value={editingExpense.vat_rate || 19}
                                    onChange={e => setEditingExpense({ ...editingExpense, vat_rate: parseInt(e.target.value) })}
                                >
                                    <option value={19}>19%</option>
                                    <option value={7}>7%</option>
                                    <option value={0}>0%</option>
                                </select>
                            </div>
                            <div className="col-md-3 mb-2">
                                <label>Kategorie</label>
                                <select
                                    className="form-control"
                                    value={editingExpense.category || ''}
                                    onChange={e => setEditingExpense({ ...editingExpense, category: e.target.value })}
                                >
                                    {categories.filter(c => c.type === 'expense').map(c => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-4 mb-2">
                                <label>Aussteller</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={editingExpense.vendor || ''}
                                    onChange={e => setEditingExpense({ ...editingExpense, vendor: e.target.value })}
                                />
                            </div>
                            <div className="col-md-8 mb-2">
                                <label>Beschreibung</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={editingExpense.description || ''}
                                    onChange={e => setEditingExpense({ ...editingExpense, description: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mt-2">
                            <button className="btn btn-success me-2" onClick={() => updateExpense(editingExpense)}>Speichern</button>
                            <button className="btn btn-secondary" onClick={() => setEditingExpense(null)}>Abbrechen</button>
                        </div>
                    </div>
                </div>
            )}

            <table className="table table-striped">
                <thead>
                    <tr>
                        <th>Datum</th>
                        <th>Betrag</th>
                        <th>MwSt</th>
                        <th>Aussteller</th>
                        <th>Kategorie</th>
                        <th>Beschreibung</th>
                        <th>Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    {expenses.map(exp => (
                        <tr key={exp.id}>
                            <td>{formatDate(exp.date)}</td>
                            <td>{formatCurrency(exp.amount)}</td>
                            <td>{formatCurrency(exp.vat_amount)} ({exp.vat_rate}%)</td>
                            <td>{exp.vendor || '-'}</td>
                            <td><span className="badge bg-secondary">{exp.category}</span></td>
                            <td>{exp.description || '-'}</td>
                            <td>
                                <button className="btn btn-sm btn-outline-primary me-1" onClick={() => setEditingExpense(exp)}>Bearbeiten</button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => deleteExpense(exp.id)}>Loeschen</button>
                            </td>
                        </tr>
                    ))}
                    {expenses.length === 0 && (
                        <tr><td colSpan="7" className="text-center text-muted">Keine Belege vorhanden</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    // Bank Tab
    const renderBank = () => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Banktransaktionen</h4>
                <div>
                    <input
                        type="file"
                        id="csvImport"
                        accept=".csv"
                        onChange={handleCsvImport}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="csvImport" className="btn btn-primary">
                        {importingCsv ? 'Importiere...' : 'CSV importieren'}
                    </label>
                </div>
            </div>

            <table className="table table-striped">
                <thead>
                    <tr>
                        <th>Datum</th>
                        <th>Betrag</th>
                        <th>Beschreibung</th>
                        <th>Verwendungszweck</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {transactions.map(tx => (
                        <tr key={tx.id} className={tx.amount >= 0 ? 'table-success' : 'table-danger'}>
                            <td>{formatDate(tx.date)}</td>
                            <td>{formatCurrency(tx.amount)}</td>
                            <td>{tx.description || '-'}</td>
                            <td>{tx.reference || '-'}</td>
                            <td>
                                {tx.is_matched ? (
                                    <span className="badge bg-success">Zugeordnet</span>
                                ) : (
                                    <span className="badge bg-warning">Offen</span>
                                )}
                            </td>
                        </tr>
                    ))}
                    {transactions.length === 0 && (
                        <tr><td colSpan="5" className="text-center text-muted">Keine Transaktionen vorhanden</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    // EUeR Tab
    const renderEur = () => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Einnahmen-Ueberschuss-Rechnung</h4>
                <div className="d-flex gap-2">
                    <select
                        className="form-control"
                        value={selectedYear}
                        onChange={e => setSelectedYear(parseInt(e.target.value))}
                        style={{ width: '100px' }}
                    >
                        {[2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <button className="btn btn-primary" onClick={loadEurReport} disabled={loading}>
                        {loading ? 'Laden...' : 'Berechnen'}
                    </button>
                </div>
            </div>

            {eurReport && (
                <div className="row">
                    <div className="col-md-6">
                        <div className="card mb-3">
                            <div className="card-header bg-success text-white">Einnahmen</div>
                            <div className="card-body">
                                <h3>{formatCurrency(eurReport.einnahmen?.total)}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card mb-3">
                            <div className="card-header bg-danger text-white">Ausgaben</div>
                            <div className="card-body">
                                <h3>{formatCurrency(eurReport.ausgaben?.total)}</h3>
                                {eurReport.ausgaben?.nach_kategorie?.length > 0 && (
                                    <ul className="list-unstyled mt-2 small">
                                        {eurReport.ausgaben.nach_kategorie.map((k, i) => (
                                            <li key={i}>{k.kategorie}: {formatCurrency(k.betrag)}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header bg-primary text-white">Gewinn / Verlust</div>
                            <div className="card-body">
                                <h2 className={eurReport.gewinn >= 0 ? 'text-success' : 'text-danger'}>
                                    {formatCurrency(eurReport.gewinn)}
                                </h2>
                                <p className="text-muted small mb-0">
                                    Vorsteuer: {formatCurrency(eurReport.vorsteuer)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // USt Tab
    const renderUst = () => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>USt-Voranmeldung</h4>
                <div className="d-flex gap-2">
                    <select
                        className="form-control"
                        value={selectedYear}
                        onChange={e => setSelectedYear(parseInt(e.target.value))}
                        style={{ width: '100px' }}
                    >
                        {[2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <select
                        className="form-control"
                        value={selectedQuarter}
                        onChange={e => setSelectedQuarter(parseInt(e.target.value))}
                        style={{ width: '80px' }}
                    >
                        <option value={1}>Q1</option>
                        <option value={2}>Q2</option>
                        <option value={3}>Q3</option>
                        <option value={4}>Q4</option>
                    </select>
                    <button className="btn btn-primary" onClick={loadUstReport} disabled={loading}>
                        {loading ? 'Laden...' : 'Berechnen'}
                    </button>
                </div>
            </div>

            {ustReport && (
                <div className="card">
                    <div className="card-header">
                        Zeitraum: {formatDate(ustReport.zeitraum?.von)} - {formatDate(ustReport.zeitraum?.bis)}
                    </div>
                    <div className="card-body">
                        <table className="table">
                            <tbody>
                                <tr>
                                    <td>Umsaetze (19%)</td>
                                    <td className="text-end">{formatCurrency(ustReport.umsaetze_19)}</td>
                                </tr>
                                <tr>
                                    <td>Umsatzsteuer (19%)</td>
                                    <td className="text-end">{formatCurrency(ustReport.umsatzsteuer_19)}</td>
                                </tr>
                                <tr>
                                    <td>Vorsteuer</td>
                                    <td className="text-end text-success">- {formatCurrency(ustReport.vorsteuer)}</td>
                                </tr>
                                <tr className="table-primary">
                                    <th>Zahllast</th>
                                    <th className="text-end">{formatCurrency(ustReport.zahllast)}</th>
                                </tr>
                            </tbody>
                        </table>
                        <p className="text-muted small">
                            Faellig am: <strong>{formatDate(ustReport.faellig_am)}</strong>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );

    // Fristen Tab
    const renderFristen = () => (
        <div>
            <h4 className="mb-3">Fristen & Erinnerungen</h4>

            <div className="card mb-3">
                <div className="card-body">
                    <h6>Neue Frist hinzufuegen</h6>
                    <div className="row">
                        <div className="col-md-3 mb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Titel"
                                value={newReminder.title}
                                onChange={e => setNewReminder({ ...newReminder, title: e.target.value })}
                            />
                        </div>
                        <div className="col-md-2 mb-2">
                            <input
                                type="date"
                                className="form-control"
                                value={newReminder.due_date}
                                onChange={e => setNewReminder({ ...newReminder, due_date: e.target.value })}
                            />
                        </div>
                        <div className="col-md-2 mb-2">
                            <select
                                className="form-control"
                                value={newReminder.type}
                                onChange={e => setNewReminder({ ...newReminder, type: e.target.value })}
                            >
                                <option value="ust">USt</option>
                                <option value="est">ESt</option>
                                <option value="gewerbe">Gewerbe</option>
                                <option value="sonstige">Sonstige</option>
                            </select>
                        </div>
                        <div className="col-md-3 mb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Notizen"
                                value={newReminder.notes}
                                onChange={e => setNewReminder({ ...newReminder, notes: e.target.value })}
                            />
                        </div>
                        <div className="col-md-2 mb-2">
                            <button className="btn btn-success w-100" onClick={addReminder}>Hinzufuegen</button>
                        </div>
                    </div>
                </div>
            </div>

            <table className="table">
                <thead>
                    <tr>
                        <th></th>
                        <th>Titel</th>
                        <th>Faellig</th>
                        <th>Typ</th>
                        <th>Notizen</th>
                    </tr>
                </thead>
                <tbody>
                    {reminders.map(r => (
                        <tr key={r.id} className={r.is_completed ? 'text-muted' : ''}>
                            <td>
                                <input
                                    type="checkbox"
                                    checked={r.is_completed}
                                    onChange={() => toggleReminderComplete(r)}
                                />
                            </td>
                            <td style={{ textDecoration: r.is_completed ? 'line-through' : 'none' }}>{r.title}</td>
                            <td>{formatDate(r.due_date)}</td>
                            <td><span className="badge bg-secondary">{r.type}</span></td>
                            <td>{r.notes || '-'}</td>
                        </tr>
                    ))}
                    {reminders.length === 0 && (
                        <tr><td colSpan="5" className="text-center text-muted">Keine Fristen vorhanden</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    // Chat Tab
    const renderChat = () => (
        <div>
            <h4 className="mb-3">Steuer-Assistent</h4>

            <div className="card" style={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
                <div className="card-body" style={{ flex: 1, overflowY: 'auto' }}>
                    {chatMessages.length === 0 && (
                        <p className="text-muted text-center">
                            Frag mich etwas zu deiner Buchhaltung, z.B.:<br />
                            "Wie hoch ist mein Gewinn dieses Jahr?"<br />
                            "Wann ist die naechste USt-Voranmeldung faellig?"<br />
                            "Welche Ausgaben kann ich absetzen?"
                        </p>
                    )}
                    {chatMessages.map((msg, i) => (
                        <div
                            key={i}
                            className={`mb-2 p-2 rounded ${msg.role === 'user' ? 'bg-primary text-white ms-5' : 'bg-light me-5'}`}
                        >
                            {msg.content}
                        </div>
                    ))}
                    {chatLoading && (
                        <div className="text-center text-muted">
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Denke nach...
                        </div>
                    )}
                </div>
                <div className="card-footer">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nachricht eingeben..."
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            onKeyPress={e => e.key === 'Enter' && sendChatMessage()}
                            disabled={chatLoading}
                        />
                        <button className="btn btn-primary" onClick={sendChatMessage} disabled={chatLoading}>
                            Senden
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div>
            <h3 className="mb-4">Buchhaltung</h3>

            {error && (
                <div className="alert alert-danger alert-dismissible fade show">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
            )}
            {success && (
                <div className="alert alert-success alert-dismissible fade show">
                    {success}
                    <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
                </div>
            )}

            <ul className="nav nav-tabs mb-4">
                {[
                    { id: 'dashboard', label: 'Uebersicht' },
                    { id: 'belege', label: 'Belege' },
                    { id: 'bank', label: 'Bank' },
                    { id: 'eur', label: 'EUeR' },
                    { id: 'ust', label: 'USt' },
                    { id: 'fristen', label: 'Fristen' },
                    { id: 'chat', label: 'Chat' }
                ].map(tab => (
                    <li key={tab.id} className="nav-item">
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'belege' && renderBelege()}
            {activeTab === 'bank' && renderBank()}
            {activeTab === 'eur' && renderEur()}
            {activeTab === 'ust' && renderUst()}
            {activeTab === 'fristen' && renderFristen()}
            {activeTab === 'chat' && renderChat()}
        </div>
    );
}

export default BuchhaltungManager;
